import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';

interface ApiUser {
  row_id: string;
  nombre_completo: string;
  nivel_permiso: string;
  rol: string;
}

interface DisplayUser {
  row_id: string;
  name: string;
  letter: string;
  color: string;
}

function avatarColor(nivel: string, name: string): string {
  if (['Christopher Vesco', 'Ileana Pinto'].includes(name)) return 'var(--status-info)';
  if (['Arnoldo Cedeño', 'Dr. Murillo'].includes(name)) return 'var(--text-tertiary)';
  return 'var(--status-positive)';
}

// LATAM convention: most people have 2 apellidos (paterno + materno).
// Display name = first name + apellido paterno (parts[0] + parts[-2]).
// For 1 or 2 word names, return as-is.
function displayName(full: string): string {
  const parts = full.trim().split(/\s+/);
  if (parts.length <= 2) return full;
  return `${parts[0]} ${parts[parts.length - 2]}`;
}

export function PinLogin() {
  const [users, setUsers] = useState<DisplayUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<DisplayUser | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const { login } = useAuth();

  // Fetch users from API on mount
  useEffect(() => {
    api.get<ApiUser[]>('auth/usuarios')
      .then((data) => {
        setUsers(
          data.map((u) => {
            const shortName = displayName(u.nombre_completo);
            return {
              row_id: u.row_id,
              name: shortName,
              letter: shortName.charAt(0).toUpperCase(),
              color: avatarColor(u.nivel_permiso, u.nombre_completo),
            };
          }),
        );
      })
      .catch(() => setError('Error cargando usuarios'))
      .finally(() => setLoadingUsers(false));
  }, []);

  const handleDigit = useCallback(
    async (digit: string) => {
      if (!selectedUser || loading) return;
      const next = pin + digit;
      setPin(next);
      setError('');

      if (next.length === 4) {
        setLoading(true);
        try {
          await login(selectedUser.row_id, next);
        } catch {
          setError('PIN incorrecto');
          setPin('');
        } finally {
          setLoading(false);
        }
      }
    },
    [selectedUser, pin, login, loading],
  );

  const handleDelete = () => setPin((p) => p.slice(0, -1));

  if (!selectedUser) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex flex-col items-center justify-center p-6">
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <svg width="32" height="32" viewBox="0 0 30 30" fill="none">
              <rect width="30" height="30" rx="8" fill="var(--status-positive)" />
              <path d="M6 22V8l4 7 5-9 5 9 4-7v14" stroke="#FFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-xl font-bold text-[var(--text-primary)]">
              MANGA<sup className="text-[11px] font-semibold text-[var(--text-tertiary)]">v1</sup>
            </span>
          </div>
          <p className="text-sm text-[var(--text-tertiary)]">Seleccione su usuario</p>
        </div>
        <div className="flex flex-col gap-3 w-full max-w-sm">
          {loadingUsers ? (
            <div className="text-center text-[var(--text-tertiary)] py-8">Cargando...</div>
          ) : (
            users.map((u) => (
              <button
                key={u.row_id}
                onClick={() => setSelectedUser(u)}
                className="flex items-center gap-3 p-4 bg-[var(--surface-elevated)] border border-[var(--border-default)] rounded-xl cursor-pointer transition-all duration-150 active:scale-[0.98] hover:bg-[var(--bg-secondary)]"
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
                  style={{ background: u.color }}
                >
                  {u.letter}
                </div>
                <span className="text-[16px] font-semibold text-[var(--text-primary)]">
                  {u.name}
                </span>
              </button>
            ))
          )}
          {error && !selectedUser && (
            <div className="text-[var(--status-danger)] text-sm font-medium text-center">{error}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex flex-col items-center justify-center p-6">
      <button
        onClick={() => { setSelectedUser(null); setPin(''); setError(''); }}
        className="self-start mb-6 text-[var(--status-info)] font-semibold text-sm bg-transparent border-none cursor-pointer"
      >
        ← Cambiar usuario
      </button>

      <div
        className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-3"
        style={{ background: selectedUser.color }}
      >
        {selectedUser.letter}
      </div>
      <div className="text-lg font-bold text-[var(--text-primary)] mb-1">{selectedUser.name}</div>
      <div className="text-sm text-[var(--text-tertiary)] mb-6">Ingrese su PIN</div>

      {/* PIN dots */}
      <div className="flex gap-3 mb-6">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`w-4 h-4 rounded-full transition-all duration-150 ${i < pin.length ? 'bg-[var(--status-info)] scale-110' : 'bg-[var(--border-default)]'}`}
          />
        ))}
      </div>

      {error && <div className="text-[var(--status-danger)] text-sm font-medium mb-4">{error}</div>}

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-3 w-full max-w-[260px]">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'].map((key) => {
          if (key === '') return <div key="empty" />;
          if (key === 'del') {
            return (
              <button
                key="del"
                onClick={handleDelete}
                className="h-16 rounded-xl bg-transparent border-none text-[var(--text-tertiary)] text-lg font-semibold cursor-pointer"
              >
                ⌫
              </button>
            );
          }
          return (
            <button
              key={key}
              onClick={() => handleDigit(key)}
              disabled={loading}
              className="h-16 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-default)] text-[var(--text-primary)] text-2xl font-semibold cursor-pointer transition-all duration-150 active:scale-95 active:bg-[var(--bg-tertiary)]"
            >
              {key}
            </button>
          );
        })}
      </div>
    </div>
  );
}
